import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useCallback } from 'react';
import { format, subMonths } from 'date-fns';
import AnalysisPage from '@/features/agent/analysis/components/AnalysisPage';

// 過去1ヶ月のデフォルト日付を生成するヘルパー
// date-fns でローカル時刻基準にフォーマットする（toISOString は UTC で日付ズレが起きる）
function getDefaultFrom(): string {
  return format(subMonths(new Date(), 1), 'yyyy-MM-dd');
}

function getDefaultTo(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * URL の検索パラメータが要素1個のとき、TanStack Router のパーサが
 * 配列ではなく単一値（string / number）として渡してくることがある。
 * z.array(...) のままだと .catch にフォールバックして選択が失われるため、
 * 単一値を [v] に正規化してから配列として検証する。
 */
const asArray = <T,>(v: unknown): T[] | unknown => {
  if (v == null) return v;
  return Array.isArray(v) ? v : [v];
};

// search params の Zod バリデーションスキーマ
export const analysisSearchSchema = z.object({
  tab: z.enum(['progress', 'insight']).catch('progress'),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .catch(() => getDefaultFrom()),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .catch(() => getDefaultTo()),
  granularity: z.enum(['day', 'week', 'month']).catch('day'),
  metric: z.enum(['uu', 'count']).catch('uu'),
  count_mode: z.enum(['total', 'registered_cohort']).catch('total'),
  // インサイトタブで表示する指標（リードタイム / 売上）の切り替え
  insight_metric: z.enum(['lead_time', 'sales']).catch('lead_time'),
  // 売上トレンドチャートの表示系列（売上見込み / 決定売上）の切り替え
  sales_trend_mode: z.enum(['forecast', 'confirmed']).catch('forecast'),
  group_by: z
    .enum(['candidate_user', 'company_user', 'job_user', 'company'])
    .optional()
    .catch(undefined),
  filter_organization_user_ids: z
    .preprocess(asArray, z.array(z.coerce.number()))
    .optional()
    .catch([]),
  filter_target: z.enum(['candidate', 'company']).catch('candidate'),
  filter_company_tag_ids: z.preprocess(asArray, z.array(z.coerce.number())).optional().catch([]),
  filter_job_tag_ids: z.preprocess(asArray, z.array(z.coerce.number())).optional().catch([]),
  filter_candidate_tag_ids: z.preprocess(asArray, z.array(z.coerce.number())).optional().catch([]),
  visible_steps: z.preprocess(asArray, z.array(z.string())).optional().catch(undefined),
  page: z.coerce.number().int().min(1).catch(1),
});

export type AnalysisSearch = z.infer<typeof analysisSearchSchema>;

export const Route = createFileRoute('/agent/analysis')({
  component: Analysis,
  staticData: { title: '分析' },
  validateSearch: analysisSearchSchema,
});

function Analysis() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const updateSearch = useCallback(
    (updates: Partial<AnalysisSearch>) => {
      void navigate({
        search: (prev) => ({ ...prev, ...updates }),
      });
    },
    [navigate]
  );

  return <AnalysisPage search={search} onUpdateSearch={updateSearch} />;
}
